import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import i18n from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import { safeGetItem, safeSetItem } from '@/lib/safeBrowser';
import { CurrencyCode, setGlobalCurrency, getGlobalCurrency, AVAILABLE_CURRENCIES } from '@/lib/formatters';

export type Language = 'en' | 'pt-BR';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  updateLanguageInDatabase: (language: Language) => Promise<void>;
  currency: CurrencyCode;
  setCurrency: (currency: CurrencyCode) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = safeGetItem('app-language');
    return (saved as Language) || 'pt-BR';
  });

  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    return getGlobalCurrency();
  });

  // Update language in the database for the current user
  const updateLanguageInDatabase = useCallback(async (lang: Language) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('LanguageContext: No user logged in, skipping database update');
        return;
      }

      const { error } = await supabase
        .from('user_profiles')
        .update({ preferred_language: lang })
        .eq('auth_user_id', user.id);

      if (error) {
        console.error('LanguageContext: Error updating language in database:', error);
      } else {
        console.log('LanguageContext: Language updated in database:', lang);
      }
    } catch (error) {
      console.error('LanguageContext: Error updating language:', error);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    console.log('LanguageContext: Setting language to:', lang);
    setLanguageState(lang);
    safeSetItem('app-language', lang);
    i18n.changeLanguage(lang);
    
    // Also update in database for persistence across devices
    updateLanguageInDatabase(lang);
  }, [updateLanguageInDatabase]);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
    setGlobalCurrency(code);
  }, []);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, updateLanguageInDatabase, currency, setCurrency }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export const LANGUAGE_OPTIONS: { value: Language; label: string; flag: string }[] = [
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'pt-BR', label: 'Português (Brasil)', flag: '🇧🇷' },
];

export { AVAILABLE_CURRENCIES };
export type { CurrencyCode };
